import {
    getObjectById,
    IEventrFlight,
    IOptionsWithTypes,
    tryParseJSON,
} from '@eventr/common'
import { ColoursObj, Gutters, Icon, Typography } from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { DataAPI } from '../../data/Data'
import { NavigationView } from '../../feat/layout/NavigationView'
import { cardTranslation } from '../../i18n/translateHelpers'
import { bs, useBoundStore } from '../../state/bound.store'
import { reactAppStage } from '../../utils/reactAppStage'
import { FlightAirports } from '../componentCards/flightComponents/flightAirports'
import { FlightPassengers } from '../componentCards/flightComponents/flightPassengers'
import { FlightTimes } from '../componentCards/flightComponents/flightTimes'
import { useMatchTagsAndUsers } from '../componentCards/hooks/useMatchTagsAndUsers'
import { AccentBox } from '../componentCards/subComponents/accentBox'
import { CardText } from '../componentCards/subComponents/cardText'
import { PageDivider } from '../componentCards/subComponents/divider'
import { VisibilityIcon } from '../componentCards/subComponents/visibilityRow'
import { useFormatValue } from '../hooks/useFormatValue'
import { AdditionalInfo } from './flightComponents/additionalInfo'
import { FlightDetails } from './flightComponents/details'

export const FlightPage = () => {
    const { _id } = useParams()
    const { formatValue } = useFormatValue()
    const { matchTagsAndUser } = useMatchTagsAndUsers()

    const { flightSection, apiUpdate, selectedItinID, org } = useBoundStore(
        useShallow((s) => {
            return {
                flightSection: (s?.selectedItinerary?.v2 ?? []).find(
                    (s) => s.sectionID === 2
                ),
                apiUpdate: (s?.apiUpdates?.flights ?? []).find(
                    (f) => f.object_ID === _id
                ),
                selectedItinID: s.selectedItinID,
                org: s.selectedOrg,
            }
        })
    )

    const flight = useMemo(() => {
        return getObjectById(
            _id ?? '',
            flightSection?.data?.flights ?? []
        ) as IEventrFlight
    }, [_id, flightSection?.data?.flights])

    useEffect(() => {
        if (!flight) return
        DataAPI.getFlightData(flight.bookref, flight.flightDate.split('T')[0], {
            itineraryId: selectedItinID,
            orgId: org?.name ?? '',
            flightId: flight._id,
            env: reactAppStage(),
        }).then(() => {
            setTimeout(() => {
                bs().getApiUpdates()
            }, 200)
        })
    }, [flight, selectedItinID, org?.name])

    const allPassengers = useMemo(() => {
        const all: IOptionsWithTypes[] = []
        flight.passengers_list?.forEach((passenger) => {
            all.push(...(passenger.users ?? []))
        })
        return all
    }, [flight])

    const files = useMemo(() => {
        if (!flight.extra_docs) return []
        return tryParseJSON(flight.extra_docs, []) as string[]
    }, [flight])

    const passenger = useMemo(() => {
        if ((flight?.passengers_list?.length ?? 0) === 0) {
            return null
        }
        return flight?.passengers_list?.find((passenger) => {
            return passenger.users?.some((user) => {
                return matchTagsAndUser(user)
            })
        })
    }, [flight, matchTagsAndUser])
    const personalFiles = useMemo(() => {
        if (!passenger?.documents) return []
        return tryParseJSON(passenger.documents, []) as string[]
    }, [passenger])
    return (
        <NavigationView
            navigationBarTitle={flight.bookref}
            backButtonAction={() => {
                window.goBack()
            }}
        >
            <Gutters>
                <Stack>
                    {flight.visibility === 'Private' && (
                        <Stack
                            paddingBottom={'16px'}
                            direction={'row'}
                            gap={'8px'}
                        >
                            <VisibilityIcon visibility={flight.visibility} />
                            <Typography
                                color={ColoursObj.core.secondary}
                                variant="bodySubtextBold"
                            >
                                {cardTranslation('Private')}
                            </Typography>
                        </Stack>
                    )}
                    <Stack
                        paddingBottom={'8px'}
                        direction={'row'}
                        justifyContent={'space-between'}
                    >
                        <Stack direction={'row'} gap={'8px'}>
                            <Icon
                                name={'Flight'}
                                iconColour={ColoursObj.core.primary}
                            />
                            <CardText variant="bodyBold">
                                {flight.bookref}
                            </CardText>
                        </Stack>
                        <CardText>
                            {cardTranslation('check-in')}{' '}
                            <b>{formatValue(flight.check_in, 'time')}</b>
                        </CardText>
                    </Stack>
                    <AccentBox
                        style={{
                            padding: '16px',
                            marginRight: '-20px',
                            marginLeft: '-20px',
                        }}
                    >
                        <FlightTimes
                            originAirportABR={flight.outboundAirportAbr}
                            destinationAirportABR={flight.inboundAirportAbr}
                            destinationTime={flight.arrival.arr_time}
                            originTime={flight.departure.dep_time}
                            timeVariant="bodySubtextBold"
                            abrVariant="bodySubtextBold"
                            gap="4px"
                            withDates
                        />
                        <Box height={'8px'} />
                        <FlightAirports
                            origin={flight.departure.dep_name}
                            destination={flight.arrival.arr_name}
                            originTerminal={flight.departure.dep_terminal}
                            destinationTerminal={flight.arrival.arr_terminal}
                        />
                    </AccentBox>
                    <FlightDetails
                        revisedTime={
                            apiUpdate?.data?.departure.revisedTime ??
                            flight.departure.revisedTime
                        }
                        departureTime={flight.departure.dep_time}
                        baggage_allowance={passenger?.baggage_allowance}
                        notes={passenger?.notes}
                        flightStatus={
                            apiUpdate?.data?.status ?? flight.flightStatus
                        }
                        personalFiles={personalFiles}
                        booking_ref={passenger?.booking_ref}
                        gate={
                            apiUpdate?.data?.departure?.gate ??
                            flight.departure.dep_gate
                        }
                        checkInDesk={
                            apiUpdate?.data?.departure?.depCheckInDesk ??
                            flight.departure.checkInDesk ??
                            flight.departure.depCheckInDesk
                        }
                    />
                    <AdditionalInfo
                        website={flight.website}
                        files={files}
                        airline={flight.airline}
                        notes={flight.notes}
                    />
                    {allPassengers.length > 0 && (
                        <Stack paddingTop="8px" gap={'8px'}>
                            <PageDivider />
                            <FlightPassengers passengers={allPassengers} />
                        </Stack>
                    )}
                </Stack>
            </Gutters>
        </NavigationView>
    )
}
